"use client";
import React from "react";
import { Tag, Typography, Row, Col, Card, Space } from "antd";

const { Text, Title } = Typography;

const Preview = (props) => {
  const {
    formData,
    legalFormData,
    genderData,
    officeData,
    clientNonPersonConstitutionOptions,
    clientNonPersonMainBusinessLineOptions,
    customerList,
    countryData,
    addressList,
    identifactionTypeData,
    staffData,
    clientClassificationData,
    productData,
  } = props;

  // Lookup for options with { value, label } shape (legalFormData, officeData, etc.)
  const findLabel = (id, options) => {
    const match = options?.find((opt) => opt.value === id);
    return match ? match.label : "-";
  };

  // Lookup for customerList items which use { id, name } shape
  const findCustomerLabel = (id, list) => {
    const match = list?.find((item) => item.id === id);
    return match ? match.name : "-";
  };

  const formatFullDate = (date) => {
    if (!date) return "-";
    try {
      return new Date(date).toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return "-";
    }
  };

  const legalOption = formData?.generalInfo?.legalFormId;
  const agentType = formData?.generalInfo?.agentType;
  const isEntity = legalOption === 2;

  const generalInfoItems = [
    { name: "Agent Type", value: agentType },

    ...(agentType === "AGENT" || agentType === "MERCHANT"
      ? [
          {
            name: "Super Agent",
            value: findCustomerLabel(
              formData?.generalInfo?.superAgentId,
              customerList,
            ),
            isTag: true,
          },
        ]
      : []),

    ...(agentType === "SUPER_AGENT"
      ? [
          {
            name: "Assigned Agents",
            value:
              formData?.generalInfo?.listOfAgents?.length > 0
                ? formData.generalInfo.listOfAgents
                    .map((id) => findCustomerLabel(id, customerList))
                    .join(", ")
                : "-",
            isTag: true,
          },
        ]
      : []),

    {
      name: "Legal Form",
      value: findLabel(legalOption, legalFormData),
      isTag: true,
    },
    {
      name: "Branch",
      value: findLabel(formData?.generalInfo?.officeId, officeData),
      isTag: true,
    },

    ...(isEntity
      ? [
          { name: "Business Name", value: formData?.generalInfo?.fullname },
          {
            name: "Incorporation Date",
            value: formatFullDate(formData?.generalInfo?.dateOfBirth),
          },
          {
            name: "Incorporation Valid Until",
            value: formatFullDate(
              formData?.generalInfo?.incorpValidityTillDate,
            ),
          },
          {
            name: "Constitution",
            value: findLabel(
              formData?.generalInfo?.constitutionId,
              clientNonPersonConstitutionOptions,
            ),
            isTag: true,
          },
          {
            name: "Main Business Line",
            value: findLabel(
              formData?.generalInfo?.mainBusinessLineId,
              clientNonPersonMainBusinessLineOptions,
            ),
            isTag: true,
          },
        ]
      : [
          { name: "First Name", value: formData?.generalInfo?.firstname },
          { name: "Middle Name", value: formData?.generalInfo?.middlename },
          { name: "Last Name", value: formData?.generalInfo?.lastname },
          {
            name: "Gender",
            value: findLabel(formData?.generalInfo?.genderId, genderData),
            isTag: true,
          },
          {
            name: "Date of Birth",
            value: formatFullDate(formData?.generalInfo?.dateOfBirth),
          },
        ]),

    {
      name: "Status",
      value: formData?.generalInfo?.active ? "Active" : "Inactive",
      isTag: true,
    },
    {
      name: isEntity ? "Business Registration Number" : "External ID",
      value: isEntity
        ? formData?.generalInfo?.businessRegistrationNumber
        : formData?.generalInfo?.externalId,
    },
    { name: "Trading Name", value: formData?.generalInfo?.tradingName },
    {
      name: "Tax Identification Number (TIN)",
      value: formData?.generalInfo?.taxIdentificationNumber,
    },
    {
      name: "Business Description",
      value: formData?.generalInfo?.businessDescription,
    },
    {
      name: "Nature of Business",
      value: formData?.generalInfo?.natureOfBusiness,
    },
  ];

  const contactInfoItems = [
    { name: "Email", value: formData?.contactInfo?.emailAddress },
    { name: "Mobile Number", value: formData?.contactInfo?.mobileNo },

    // Nationality and ID type shown only for PERSON (not entity)
    ...(!isEntity
      ? [
          {
            name: "Nationality",
            value: findLabel(
              formData?.contactInfo?.nationality,
              countryData,
            ),
          },
          {
            name: "Identity Type",
            value: findLabel(
              formData?.contactInfo?.idTypeId,
              identifactionTypeData,
            ),
            isTag: true,
          },
        ]
      : []),

    // ID sub-fields shown when an identity type is selected
    ...(formData?.contactInfo?.idTypeId
      ? [
          { name: "ID Number", value: formData?.contactInfo?.idNo },
          {
            name: "ID Issuing Date",
            value: formatFullDate(formData?.contactInfo?.idIssuingDate),
          },
          {
            name: "Expiry Date",
            value: formatFullDate(formData?.contactInfo?.passExpDate),
          },
        ]
      : []),
  ];

  const othersInfoItems = [
    {
      name: "Is Staff",
      value: formData?.othersInfo?.isStaff ? "Yes" : "No",
    },
    {
      name: "Relationship Manager",
      value: findLabel(formData?.othersInfo?.staffId, staffData),
    },
    {
      name: "Client Classification",
      value: findLabel(
        formData?.othersInfo?.clientClassificationId,
        clientClassificationData,
      ),
      isTag: true,
    },
    ...(formData?.othersInfo?.openCasa
      ? [
          {
            name: "Wallet Product",
            value: findLabel(
              formData?.othersInfo?.savingsProductId,
              productData,
            ),
            isTag: true,
          },
        ]
      : []),
    {
      name: "Submitted On",
      value: formatFullDate(formData?.othersInfo?.submittedOnDate),
    },
  ];

  const sections = [
    { title: "General Information", items: generalInfoItems },
    { title: "Contact Information", items: contactInfoItems },
    { title: "Others", items: othersInfoItems },
  ];

  const renderValue = (item) => {
    const value = item.value || "-";
    if (item.isTag && value !== "-") {
      return (
        <Tag
          style={{
            backgroundColor: "#FDF4FF",
            border: "1px solid #F6D0FE",
            borderRadius: "16px",
            padding: "2px 12px",
          }}
        >
          <Text style={{ color: "#9F1AB1", fontWeight: 600 }}>{value}</Text>
        </Tag>
      );
    }
    return <Text style={{ color: "#1D2939", fontWeight: 500 }}>{value}</Text>;
  };

  return (
    <div style={{ width: "100%", paddingBottom: "40px" }}>
      <Title level={3}>Preview</Title>
      <Text type="secondary" style={{ display: "block", marginBottom: "32px" }}>
        Review information before submission.
      </Text>

      {sections.map((section) => (
        <div key={section.title} style={{ marginBottom: "32px" }}>
          <Title level={4} style={{ marginBottom: "16px", fontSize: "18px" }}>
            {section.title}
          </Title>
          <div
            style={{
              border: "1px solid #EAECF0",
              borderRadius: "12px",
              overflow: "hidden",
            }}
          >
            {section.items.map((item, idx) => (
              <Row
                key={item.name}
                style={{
                  borderBottom:
                    idx === section.items.length - 1
                      ? "none"
                      : "1px solid #EAECF0",
                  minHeight: "52px",
                }}
              >
                <Col
                  span={10}
                  style={{ padding: "16px 24px", backgroundColor: "#F9FAFB" }}
                >
                  <Text style={{ color: "#344054", fontWeight: 500 }}>
                    {item.name}
                  </Text>
                </Col>
                <Col
                  span={14}
                  style={{ padding: "16px 24px", backgroundColor: "#FFFFFF" }}
                >
                  {renderValue(item)}
                </Col>
              </Row>
            ))}
          </div>
        </div>
      ))}

      {/* Address Information Section */}
      <div style={{ marginBottom: "32px" }}>
        <Title level={4} style={{ marginBottom: "16px", fontSize: "18px" }}>
          Address Information
        </Title>
        <Space direction="vertical" style={{ width: "100%" }} size={16}>
          {addressList?.length > 0 ? (
            addressList.map((addr, idx) => (
              <Card
                key={idx}
                style={{
                  borderRadius: "12px",
                  border: "1px solid #EAECF0",
                  backgroundColor: "#F9FAFB",
                }}
                bodyStyle={{ padding: "16px 24px" }}
              >
                <Row justify="space-between" align="middle">
                  <Col>
                    <Text strong style={{ display: "block", fontSize: "16px" }}>
                      {addr.title || "Postal Address"}
                    </Text>
                    <Text type="secondary">
                      {addr.description || `${addr.addressLine1}, ${addr.city}`}
                    </Text>
                  </Col>
                </Row>
              </Card>
            ))
          ) : (
            <Text type="secondary">No addresses added.</Text>
          )}
        </Space>
      </div>
    </div>
  );
};

export default Preview;
